import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tag, Plus, Trash2, Percent, DollarSign, Truck, Copy, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const GlassCard = ({ children, className = '', ...props }) => (
  <div className={`bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] rounded-2xl relative overflow-hidden transition-all duration-300 ${className}`} {...props}>{children}</div>
);

const typeIcons = { percentage: Percent, fixed: DollarSign, free_shipping: Truck };
const typeLabels = { percentage: '% Off', fixed: 'Fixed $', free_shipping: 'Free Shipping' };

export default function DashboardDiscounts() {
  const [discounts, setDiscounts] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [form, setForm] = useState({
    code: '', type: 'percentage', value: 10, min_order: 0, max_uses: 0, end_date: ''
  });
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDiscounts = () => {
    axios.get(`${API}/discounts`, { headers }).then(res => setDiscounts(res.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { fetchDiscounts(); }, []);

  const handleCreate = async () => {
    if (!form.code.trim()) return toast.error('Code is required');
    try {
      await axios.post(`${API}/discounts`, form, { headers });
      toast.success('Discount created');
      setShowCreate(false);
      setForm({ code: '', type: 'percentage', value: 10, min_order: 0, max_uses: 0, end_date: '' });
      fetchDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount');
    }
  };

  const toggleActive = async (disc) => {
    await axios.put(`${API}/discounts/${disc.id}`, { is_active: !disc.is_active }, { headers });
    fetchDiscounts();
  };

  const deleteDiscount = async (id) => {
    if (!(await themedConfirm('Delete this discount?'))) return;
    await axios.delete(`${API}/discounts/${id}`, { headers });
    toast.success('Discount deleted');
    fetchDiscounts();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-6" data-testid="discounts-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Discounts</h1>
          <p className="text-sm text-slate-400 mt-1">Create promo codes and run sales</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-[#0075FF] to-[#00FFFF] text-white rounded-full px-5 py-2.5 text-sm font-medium shadow-[0_0_15px_rgba(0,117,255,0.4)] hover:shadow-[0_0_25px_rgba(0,255,255,0.6)] transition-all flex items-center gap-2" data-testid="create-discount-btn">
          <Plus size={16} /> New Discount
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <GlassCard className="p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Active Codes</p>
          <p className="text-2xl font-bold text-[#00CC99] mt-1">{discounts.filter(d => d.is_active).length}</p>
        </GlassCard>
        <GlassCard className="p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Total Uses</p>
          <p className="text-2xl font-bold text-white mt-1">{discounts.reduce((s, d) => s + (d.uses || 0), 0)}</p>
        </GlassCard>
        <GlassCard className="p-5">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Total Codes</p>
          <p className="text-2xl font-bold text-white mt-1">{discounts.length}</p>
        </GlassCard>
      </div>

      {/* Discounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {discounts.map(disc => {
          const Icon = typeIcons[disc.type] || Tag;
          return (
            <GlassCard key={disc.id} className={`p-5 ${!disc.is_active ? 'opacity-50' : ''} hover:shadow-[0_0_25px_rgba(0,255,255,0.15)] hover:border-cyan-500/20`} data-testid={`discount-card-${disc.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-gradient-to-br from-[#0075FF]/20 to-[#00FFFF]/20">
                    <Icon size={18} className="text-[#00FFFF]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <code className="text-white font-mono font-bold text-lg">{disc.code}</code>
                      <button onClick={() => copyCode(disc.code)} className="text-slate-500 hover:text-[#00FFFF]">
                        {copied === disc.code ? <Check size={14} className="text-[#00CC99]" /> : <Copy size={14} />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-400">{typeLabels[disc.type]} {disc.type !== 'free_shipping' && `— ${disc.type === 'percentage' ? disc.value + '%' : '$' + disc.value}`}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-xs text-slate-400">
                {disc.min_order > 0 && <p>Min order: ${disc.min_order}</p>}
                {disc.max_uses > 0 && <p>Uses: {disc.uses || 0} / {disc.max_uses}</p>}
                {disc.end_date && <p>Expires: {new Date(disc.end_date).toLocaleDateString()}</p>}
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/5">
                <button onClick={() => toggleActive(disc)} className={`text-xs px-3 py-1 rounded-full border ${disc.is_active ? 'bg-green-500/10 text-[#00CC99] border-green-500/20' : 'bg-white/5 text-slate-400 border-white/10'}`} data-testid={`toggle-discount-${disc.id}`}>
                  {disc.is_active ? 'Active' : 'Inactive'}
                </button>
                <button onClick={() => deleteDiscount(disc.id)} className="text-slate-500 hover:text-[#FF6B6B] transition-colors" data-testid={`delete-discount-${disc.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </GlassCard>
          );
        })}
        {discounts.length === 0 && !loading && (
          <GlassCard className="col-span-full p-16 text-center">
            <Tag size={48} className="mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">No discount codes yet. Create your first promo code!</p>
          </GlassCard>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#171A2C] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg">Create Discount Code</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="create-discount-form">
            <div>
              <Label className="text-slate-300">Code</Label>
              <Input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value.toUpperCase()}))} placeholder="SUMMER20" className="bg-white/5 border-white/10 text-white mt-1 font-mono" data-testid="discount-code-input" />
            </div>
            <div>
              <Label className="text-slate-300">Type</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {['percentage', 'fixed', 'free_shipping'].map(t => (
                  <button key={t} onClick={() => setForm(p => ({...p, type: t}))} className={`p-3 rounded-xl text-xs font-medium text-center border transition-all ${form.type === t ? 'bg-[#0075FF]/20 border-[#0075FF]/50 text-white' : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'}`} data-testid={`discount-type-${t}`}>
                    {typeLabels[t]}
                  </button>
                ))}
              </div>
            </div>
            {form.type !== 'free_shipping' && (
              <div>
                <Label className="text-slate-300">Value {form.type === 'percentage' ? '(%)' : '($)'}</Label>
                <Input type="number" value={form.value} onChange={e => setForm(p => ({...p, value: parseFloat(e.target.value) || 0}))} className="bg-white/5 border-white/10 text-white mt-1" data-testid="discount-value-input" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Min Order ($)</Label>
                <Input type="number" value={form.min_order} onChange={e => setForm(p => ({...p, min_order: parseFloat(e.target.value) || 0}))} className="bg-white/5 border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-slate-300">Max Uses (0 = unlimited)</Label>
                <Input type="number" value={form.max_uses} onChange={e => setForm(p => ({...p, max_uses: parseInt(e.target.value) || 0}))} className="bg-white/5 border-white/10 text-white mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Expiration Date (optional)</Label>
              <Input type="date" value={form.end_date} onChange={e => setForm(p => ({...p, end_date: e.target.value}))} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <button onClick={handleCreate} className="w-full bg-gradient-to-r from-[#0075FF] to-[#00FFFF] text-white rounded-full py-2.5 font-medium shadow-[0_0_15px_rgba(0,117,255,0.4)] hover:shadow-[0_0_25px_rgba(0,255,255,0.6)] transition-all" data-testid="submit-discount-btn">
              Create Discount
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
